import React, { useEffect } from 'react'
import styled from 'styled-components'
import { Modal } from 'components/common/modal'
import { Column, Row } from 'components/common/layout'
import { DescText, SemiBoldText } from 'components/common/text'
import { FiChevronRight } from 'react-icons/fi'
import { useIsDesktop } from 'utils/hooks'
import { useScoutMyRequest, useScoutRequestMyInfoFillStatus } from 'apis/hooks'
import { useRouter } from 'next/router'
import {
  getHideNoTechStackModalStorage,
  getProfileFillNeedModalStorage,
  setHideNoTechStackModalStorage,
} from 'utils/storages'
import {
  HIDE_PROFILE_FILL_NEED_MODAL_KEY,
  MILLISECONDS_PER_HOUR,
} from 'infra/constants'
import { useIsClient } from 'usehooks-ts'
import { getProfileCompleteStatus } from 'utils/formatters'
import { mutate } from 'swr'
import {
  MyProfileActionQueryValue,
  RoutePaths,
  RouterQueryKey,
} from 'types/router'
import { getScoutRequestMyInfoFillStatusType } from 'components/scouts/profile/my-profile-bottom-bar'
import { useStores } from 'stores/globals'
import { observer } from 'mobx-react'
import { ScoutProfile } from 'types/common'

const getIsProfileCompletedAll = (profile: ScoutProfile) => {
  const {
    isCompletedEducation,
    isCompletedCareer,
    isCompletedResume,
    isCompletedIntroduction,
    isCompletedExpectation,
    isCompletedLanguage,
    isCompletedJobSearchingReason,
  } = getProfileCompleteStatus(profile)
  const isExperienced = profile.workLevel === 3
  const isCompletedAll =
    isCompletedEducation &&
    isCompletedCareer &&
    isCompletedResume &&
    isCompletedIntroduction &&
    isCompletedExpectation &&
    isCompletedLanguage &&
    (isExperienced ? isCompletedJobSearchingReason : true)
  return isCompletedAll
}

// NOTE: 이 모달은 프로필 대신 작성을 완료한 후 모든 정보가 채워지지는 못했을 때 사용자에게 직접 프로필을 완성하라고 넛지하기 위함
export const ProfileFillNeedModal: React.FC = observer(() => {
  const { data: myRequest } = useScoutMyRequest()
  const { data: infoFillStatus } = useScoutRequestMyInfoFillStatus()
  const { profileFillNeedModalStore } = useStores()
  const router = useRouter()
  const isClient = useIsClient()

  useEffect(() => {
    if (
      !isClient ||
      router.query[RouterQueryKey.ACTION] ===
        MyProfileActionQueryValue.UPDATE_SKILLS || // 내 프로필 페이지에 보유 스킬 업데이트를 위해 들어온 경우 모달 표시 X
      getProfileFillNeedModalStorage() || // 나중에 하기 클릭했는지
      !myRequest ||
      getIsProfileCompletedAll(myRequest) || // 모든 정보가 채워졌는지
      !infoFillStatus?.hasCompletedRequest || // 프로필 대신 작성을 요청해서 완료된 적이 있는지
      !infoFillStatus.infoFillCompletedAt || // 프로필 대신 작성이 완료되었는지
      new Date(myRequest.updatedByUserAt).getTime() > // 프로필 대신 작성이 완료된 후 사용자가 프로필을 직접 수정한 적 있는지
        new Date(infoFillStatus.infoFillCompletedAt).getTime() ||
      getScoutRequestMyInfoFillStatusType(infoFillStatus) === 'WORKING' // 여러번 요청할 수 있어서, infoFillCompletedAt 는 있지만 최신 요청건이 아직 처리 중인지 추가로 확인
    )
      return
    profileFillNeedModalStore.setIsOpen(true)
  }, [
    isClient,
    myRequest,
    infoFillStatus,
    profileFillNeedModalStore,
    router.query,
  ])

  if (!profileFillNeedModalStore.isOpen || !myRequest) return null
  return (
    <ProfileFillNeedContent
      profile={myRequest}
      closeModal={() => profileFillNeedModalStore.setIsOpen(false)}
    />
  )
})

const ProfileFillNeedContent: React.FC<{
  profile: ScoutProfile
  closeModal: () => void
}> = observer(({ profile, closeModal }) => {
  const router = useRouter()
  const isDesktop = useIsDesktop()

  const isRequiredInfoCompleted =
    !!profile?.educations.length &&
    (!!profile.careers.length || !!profile.otherActivities.length)
  const mainTitle = isRequiredInfoCompleted
    ? '아직 작성하지 않은 정보가 있어요!'
    : '아직 스카우트 제안을 받을 수 없어요!'
  const subTitle = isRequiredInfoCompleted
    ? `입력되지 않은 정보가 있어 프로필을 완성하지 못했어요\n프로필을 완성하고 <b>더욱 매력적인</b> 스카우트 제안을 받아보세요`
    : `<b>입력되지 않은 필수 정보가</b> 있어 프로필을 공개하지 못했어요\n필수 정보를 추가하고 스카우트 제안을 받아보세요`
  return (
    <Modal width={460} disableCloseBtn disableBackgroundClick>
      <Content>
        <MainText>{mainTitle}</MainText>
        <SubText dangerouslySetInnerHTML={{ __html: subTitle }} />
      </Content>
      <Row>
        <Button
          id='subBtn'
          onClick={() => {
            localStorage.setItem(HIDE_PROFILE_FILL_NEED_MODAL_KEY, 'true')
            closeModal()
            mutate('/scouts/requests/me/drafts')
          }}
        >
          나중에 하기
        </Button>
        <Button
          onClick={() => {
            // 여기서 내 프로필 페이지로 이동하면 스킬 수집 모달 한 시간 동안 띄우지 않음
            if (getHideNoTechStackModalStorage().expiresAt < Date.now()) {
              setHideNoTechStackModalStorage({
                expiresAt: Date.now() + MILLISECONDS_PER_HOUR,
              })
            }
            localStorage.setItem(HIDE_PROFILE_FILL_NEED_MODAL_KEY, 'true')
            closeModal()
            router.push({
              pathname: RoutePaths.MY_PROFILE,
              query: { [RouterQueryKey.RESOLVED_INFO_FILL]: 'true' },
            })
            mutate('/scouts/requests/me/drafts')
          }}
        >
          프로필 완성하러 가기 <FiChevronRight size={isDesktop ? 24 : 18} />
        </Button>
      </Row>
    </Modal>
  )
})

const Content = styled(Column)`
  gap: 24px;
  text-align: center;
  padding: 60px 0 40px;
`

const Button = styled(Row)`
  gap: 4px;
  width: 100%;
  padding: 32px 0;
  justify-content: center;
  color: ${(p) => p.theme.color.white1};
  font-size: ${(p) => p.theme.fontSize.desc};
  font-weight: ${(p) => p.theme.fontWeight.semiBold};
  background-color: ${(p) => p.theme.color.secondary};
  border-top: 1px solid ${(p) => p.theme.color.secondary};
  cursor: pointer;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.small};
  }
  &#subBtn {
    color: ${(p) => p.theme.color.gray2};
    font-weight: ${(p) => p.theme.fontWeight.medium};
    background-color: ${(p) => p.theme.color.white1};
    border-top: 1px solid ${(p) => p.theme.color.line};
  }
`

const MainText = styled(SemiBoldText)`
  font-size: ${(p) => p.theme.fontSize.h3};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.h5};
  }
`

const SubText = styled(DescText)`
  font-weight: ${(p) => p.theme.fontWeight.regular};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.small};
    line-height: 18px;
  }
`
